import DotsHorizontalIcon from 'mdi-react/DotsHorizontalIcon';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Button,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Table,
  UncontrolledDropdown,
  Badge,
  Nav,
  NavItem,
  NavLink,
} from 'reactstrap';
import ArrowUpIcon from 'mdi-react/ArrowUpIcon';
import ArrowDownIcon from 'mdi-react/ArrowDownIcon';
import CloudDownloadIcon from 'mdi-react/CloudDownloadIcon';
import fileDownload from 'js-file-download';
import { Parser } from 'json2csv';
import Loading from '../../../shared/components/custom/Loading';
import Api from '../../../util/api';
import { formatCurrencyNumber } from '../../../util/functions';
import Pagination from '../../../shared/components/pagination/Pagination';
import t, { partial } from '../../../util/translation/translation';
import useConfig from '../../../util/useConfig';
import { formatDateTime } from '../../../util/date-and-time';
import useModal from '../../../shared/hooks/useModal';
import DeleteModal from '../../../shared/components/custom/DeleteModal';

const TransactionsList = () => {
  const p = partial('transactions');
  const s = partial('shared');
  const po = partial('paymentOptions');
  const config = useConfig();
  const [transactions, setTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(true);
  const [loadingInvoice, setLoadingInvoice] = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);
  const [filter, setFilter] = useState('all');
  const [handleModal, visible, deleteObject] = useModal();
  const pills = [
    {
      label: s('all'),
      value: 'all',
    },
    {
      label: p('tier'),
      value: 'tier',
    },
    {
      label: p('auctionItem'),
      value: 'auction_item',
    },
    {
      label: p('goalSponsor'),
      value: 'goal_subscription',
    },
    {
      label: p('lotteryTicket'),
      value: 'lottery_ticket',
    },
    {
      label: p('eventBooking'),
      value: 'event_booking',
    },
  ];

  const [pagination, setPagination] = useState({
    total: 0,
    currentPage: 1,
    perPage: 10,
  });
  const [order, setOrder] = useState({
    orderBy: 'createdAt',
    desc: true,
  });
  const [tab, setTab] = useState(1);
  const exportPageSize = 100;

  const attachUsersToTransactions = async (transactionRows) => {
    const userIds = [
      ...new Set(
        transactionRows
          .map((item) => item.userId)
          .filter((userId) => userId != null)
      ),
    ];

    if (!userIds.length) {
      return transactionRows;
    }

    const { data: usersByIds } = await Api.users.getUsersByCloudIds(userIds);
    const userByIdMap = {};
    for (let i = 0; i < usersByIds.length; i++) {
      userByIdMap[usersByIds[i].userCloudId] = usersByIds[i];
    }

    return transactionRows.map((transaction) => ({
      ...transaction,
      user: userByIdMap[transaction.userId],
    }));
  };

  const fetchTransactions = async (
    currentPage = 1,
    pageSize = 10,
    orderObject = {
      orderBy: 'createdAt',
      desc: true,
    },
    filter = 'all'
  ) => {
    setLoadingTransactions(true);
    try {
      const resTransactions = await Api.transactions.getAllTransactions(
        { page: currentPage, take: pageSize },
        {
          orderBy: orderObject.orderBy,
          direction: orderObject.desc ? 'DESC' : 'ASC',
        },
        filter
      );
      const mappedTransactions = await attachUsersToTransactions(
        resTransactions.data
      );
      setTransactions(mappedTransactions);
      setPagination((old) => ({ ...old, total: resTransactions.count }));
      setLoadingTransactions(false);
    } catch (err) {
      toast.error(p('transactionsRetrievingERROR'));
      setLoadingTransactions(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, []);

  const handleChangeFilter = (value) => {
    setFilter(value);
    setPagination({
      total: 0,
      currentPage: 1,
      perPage: 10,
    });
    fetchTransactions(1, 10, order, value);
  };

  const onChangePage = (pager) => {
    if (
      pager.pageSize !== pagination.perPage ||
      pager.currentPage !== pagination.currentPage
    ) {
      setPagination((old) => ({
        ...old,
        currentPage: pager.currentPage,
        perPage: pager.pageSize,
      }));
      fetchTransactions(pager.currentPage, pager.pageSize, order, filter);
    }
  };

  const renderSort = (cellOrder) => {
    if (order.orderBy !== cellOrder) {
      return undefined;
    }
    return order.desc ? <ArrowDownIcon size={16} /> : <ArrowUpIcon size={16} />;
  };
  const handleSort = (incommingOrder) => {
    const newOrder = {
      orderBy: incommingOrder,
      desc: incommingOrder === order.orderBy ? !order.desc : true,
    };
    setOrder(newOrder);
    setPagination({
      total: 0,
      currentPage: 1,
      perPage: 10,
    });
    fetchTransactions(1, 10, newOrder, filter);
  };
  const getUserName = (transaction) => {
    if (!transaction.userId) {
      return transaction.userEmail;
    }

    const userName = [transaction.user?.firstname, transaction.user?.lastname]
      .filter(Boolean)
      .join(' ');

    return userName || transaction.userEmail;
  };
  const fetchAllTransactionsForExport = async () => {
    const requestOrder = {
      orderBy: order.orderBy,
      direction: order.desc ? 'DESC' : 'ASC',
    };
    const firstPage = await Api.transactions.getAllTransactions(
      { page: 1, take: exportPageSize },
      requestOrder,
      filter
    );
    const pageCount = Math.ceil(firstPage.count / exportPageSize);
    const remainingPages = Array.from(
      { length: Math.max(pageCount - 1, 0) },
      (_, index) => index + 2
    );
    const remainingResults = await Promise.all(
      remainingPages.map((page) =>
        Api.transactions.getAllTransactions(
          { page, take: exportPageSize },
          requestOrder,
          filter
        )
      )
    );

    return attachUsersToTransactions([
      ...firstPage.data,
      ...remainingResults.flatMap((result) => result.data),
    ]);
  };
  const handleCsvExport = async () => {
    try {
      setLoadingExport(true);
      const transactionsForExport = await fetchAllTransactionsForExport();
      const mappedTransactions = transactionsForExport.map((transaction) => ({
        date: formatDateTime(transaction.createdAt),
        entityType: getEntityName(transaction.paymentOption?.entityType),
        entityName: transaction.paymentOption?.entityName || '',
        price: `${formatCurrencyNumber(Number(transaction.price) / 100)} ${
          config.currencyCaps
        }`,
        user: getUserName(transaction),
        phone: transaction.userId ? transaction.user?.phoneNumber : '',
        email: transaction.userId
          ? transaction.user?.email
          : transaction.userEmail,
        status: p(`status-${transaction.status}`),
      }));
      const fields = [
        { label: s('date'), value: 'date' },
        { label: t('HighlightBannerPage.entity'), value: 'entityType' },
        { label: 'Entity name', value: 'entityName' },
        { label: s('price'), value: 'price' },
        { label: s('user'), value: 'user' },
        { label: s('phone'), value: 'phone' },
        { label: s('email'), value: 'email' },
        { label: p('status'), value: 'status' },
      ];
      const json2csvParser = new Parser({ fields });
      const csv = json2csvParser.parse(mappedTransactions);
      fileDownload(`\uFEFF${csv}`, `transactions-${filter}.csv`);
    } catch {
      toast.error(p('transactionsRetrievingERROR'));
    } finally {
      setLoadingExport(false);
    }
  };
  const downloadInvoice = async (transactionId) => {
    try {
      setLoadingInvoice(true);

      const receiptResponse =
        await Api.transactions.downloadInvoice(transactionId);

      const filename = `${transactionId}-invoice.pdf`;

      // Create Blob with correct MIME type
      const blob = new Blob([receiptResponse.data], {
        type: 'application/pdf',
      });

      const url = globalThis.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();

      // Cleanup
      globalThis.URL.revokeObjectURL(url);
      link.remove();

      setLoadingInvoice(false);
    } catch (error) {
      console.error('Error downloading invoice:', error);
      toast.error(p('failedToDownloadInvoice'));
      setLoadingInvoice(false);
    }
  };

  const getBadgeColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
        return 'danger';
      case 'refunded':
        return 'info';
      default:
        return 'default';
    }
  };
  const getEntityName = (entityType) => {
    switch (entityType) {
      case 'tier':
        return p('tier');
      case 'auction_item':
        return p('auctionItem');
      case 'goal_subscription':
        return p('goalSponsor');
      case 'lottery_ticket':
        return p('lotteryTicket');
      case 'event_booking':
        return p('eventBooking');
      default:
        return '';
    }
  };
  const getEntityLink = (entityType, entityId) => {
    switch (entityType) {
      case 'tier':
        return `/user_tiers/edit/${entityId}`;
      case 'auction_item':
        return `/auctions/edit/auction_house/edit/${entityId}?tab=1`;
      case 'goal_subscription':
        return `/goal-sponsor/subscription-plans/edit/${entityId}?tab=6`;
      case 'lottery_ticket':
        return `/lottery/edit/${entityId}`;
      case 'event_booking':
        return `/events/edit/${entityId}?tab=3`;
      default:
        return '';
    }
  };
  const renderTransactions = () => {
    return transactions.map((transaction, i) => (
      <tr
        style={{ background: i % 2 ? '#fff' : '#F9FAFB' }}
        key={`transaction-${transaction.id}`}
      >
        <td>{formatDateTime(transaction.createdAt)}</td>
        <td>
          <div className="flex-Vcenter">
            <Link
              to={getEntityLink(
                transaction.paymentOption?.entityType,
                transaction.paymentOption?.entityId
              )}
            >
              {getEntityName(transaction.paymentOption?.entityType)}
            </Link>
          </div>
        </td>
        <td>
          <b>
            {formatCurrencyNumber(Number(transaction.price) / 100)}{' '}
            {config.currencyCaps}
          </b>
        </td>
        <td>
          <div className="flex-Vcenter">
            {transaction.userId ? (
              <div>
                <Link
                  to={`/users/edit/${transaction.userId}`}
                >{`${transaction.user?.firstname} ${transaction.user?.lastname}`}</Link>
                <br />
                <span
                  style={{ fontSize: 10 }}
                >{`${transaction.user?.phoneNumber}`}</span>
                <br />
                <span
                  style={{ fontSize: 10 }}
                >{`${transaction.user?.email}`}</span>
              </div>
            ) : (
              <span>{transaction.userEmail}</span>
            )}
          </div>
        </td>
        <td>
          <Badge color={getBadgeColor(transaction.status)}>
            {p(`status-${transaction.status}`)}
          </Badge>
        </td>
        <td>
          <CloudDownloadIcon
            style={{ cursor: 'pointer' }}
            onClick={() => {
              downloadInvoice(transaction.id);
            }}
          />
        </td>

        <td>
          {transaction?.paymentOption?.paymentType === 'one_time' &&
            transaction.status === 'completed' && (
              <UncontrolledDropdown className="dashboard__table-more">
                <DropdownToggle>
                  <DotsHorizontalIcon />
                </DropdownToggle>
                <DropdownMenu className="dropdown__menu">
                  <DropdownItem
                    onClick={() =>
                      handleModal({
                        id: transaction.id,
                        type: transaction.paymentOption.entityType,
                      })
                    }
                    className="danger"
                  >
                    {p('refund')}
                  </DropdownItem>
                </DropdownMenu>
              </UncontrolledDropdown>
            )}
        </td>
      </tr>
    ));
  };
  const refundTransaction = async () => {
    try {
      await Api.transactions.refundTransaction(deleteObject.id);
      handleModal(false);
      fetchTransactions();
    } catch (err) {
      toast.error(p('transactionsRetrievingERROR'));
    }
  };
  const getModalType = () => {
    if (!deleteObject) return '';
    switch (deleteObject.type) {
      case 'tier':
        return p('tier');
      default:
        return '';
    }
  };
  return (
    <>
      <DeleteModal
        visible={visible}
        modalType={'refund'}
        type={getModalType()}
        handleModal={handleModal}
        modalAction={refundTransaction}
      />
      <Loading
        loading={loadingTransactions || loadingInvoice || loadingExport}
      />
      <Nav pills>
        {pills.map((pill) => (
          <NavItem key={pill.value}>
            <NavLink
              role="button"
              active={filter === pill.value}
              onClick={() => handleChangeFilter(pill.value)}
            >
              {pill.label}
            </NavLink>
          </NavItem>
        ))}
      </Nav>
      <div className="d-flex justify-content-end" style={{ marginTop: 10 }}>
        <Button
          outline
          color="primary"
          size="sm"
          onClick={handleCsvExport}
          disabled={loadingExport}
        >
          {s('export')}
        </Button>
      </div>

      <div
        style={{
          display: 'flex',
          paddingBottom: 5,
          marginTop: 10,
          background: '#fff',
          borderRadius: 6,
          boxShadow: '0px 2px 8px 0px rgba(0,0,0,0.08)',
        }}
      >
        <Table responsive striped>
          <thead>
            <tr>
              <th
                style={{ color: '#4D576A' }}
                className="sortable"
                onClick={() => handleSort('createdAt')}
              >
                <div className="flex alignItems-center">
                  {s('date')} {renderSort('createdAt')}
                </div>
              </th>
              <th style={{ color: '#4D576A' }}>
                <div className="flex alignItems-center">
                  {t('HighlightBannerPage.entity')}
                </div>
              </th>
              <th
                style={{ color: '#4D576A' }}
                className="sortable"
                onClick={() => handleSort('price')}
              >
                <div className="flex alignItems-center">
                  {s('price')} {renderSort('price')}
                </div>
              </th>
              <th style={{ color: '#4D576A' }}>
                <div className="flex alignItems-center">{s('user')}</div>
              </th>

              <th
                style={{ color: '#4D576A' }}
                className="sortable"
                onClick={() => handleSort('status')}
              >
                <div className="flex alignItems-center">
                  {p('status')} {renderSort('status')}
                </div>
              </th>
              <th style={{ color: '#4D576A' }}>
                <div className="flex alignItems-center">
                  {p('downloadInvoice')}
                </div>
              </th>
              <th />
            </tr>
          </thead>
          <tbody>{renderTransactions()}</tbody>
        </Table>
      </div>
      <Pagination
        items={pagination.total}
        onChangePage={onChangePage}
        initialPage={pagination.currentPage}
      />
    </>
  );
};

export default TransactionsList;
